import React, { useState } from 'react';
import { ScrubInput } from 'calc-controls';
import { useAppStore } from '@/store';
import type { AttendancePolicy, PenaltyTier, PolicyRuleType } from '@abs/shared-types';
import { PolicyRuleType as PRT } from '@abs/shared-types';
import { generateId } from '@/utils/id';
import { Button, Input, Select, Tooltip } from '../shared';
import type { SelectOption } from '../shared';
import { PenaltyTierEditor } from './PenaltyTierEditor';
import { PolicyTemplateSelector } from './PolicyTemplateSelector';
import type { PolicyTemplate } from '@abs/shared-types';

const RULE_TYPE_OPTIONS: SelectOption[] = [
  { value: PRT.PercentageBased, label: 'Percentage-Based' },
  { value: PRT.FixedCount, label: 'Fixed Count' },
  { value: PRT.PerAbsenceDeduction, label: 'Per-Absence Deduction' },
];

export interface PolicyConfigFormProps {
  policyId?: string;
  onSave: (policyId: string) => void;
  onCancel: () => void;
}

export function PolicyConfigForm({ policyId, onSave, onCancel }: PolicyConfigFormProps): React.ReactElement {
  const policies = useAppStore((s) => s.policies);
  const addPolicy = useAppStore((s) => s.addPolicy);
  const updatePolicy = useAppStore((s) => s.updatePolicy);

  const existing = policyId ? policies.find((p) => p.id === policyId) : null;

  const [name, setName] = useState(existing?.name ?? '');
  const [ruleType, setRuleType] = useState<PolicyRuleType>(existing?.ruleType ?? PRT.FixedCount);
  const [floor, setFloor] = useState(existing?.attendanceFloor ?? 3);
  const [floorUnit, setFloorUnit] = useState<'percentage' | 'count'>(existing?.attendanceFloorUnit ?? 'count');
  const [unexcusedGrace, setUnexcusedGrace] = useState(existing?.grace.unexcusedGrace ?? 0);
  const [excusedGrace, setExcusedGrace] = useState(existing?.grace.excusedGrace ?? 0);
  const [tardyEnabled, setTardyEnabled] = useState(existing?.tardyConversion.enabled ?? false);
  const [tardiesPer, setTardiesPer] = useState(existing?.tardyConversion.tardiesPerAbsence ?? 3);
  const [excusedLimit, setExcusedLimit] = useState<number | null>(existing?.excusedAbsenceLimit ?? null);
  const [tiers, setTiers] = useState<PenaltyTier[]>(existing?.tiers ?? []);
  const [showTemplates, setShowTemplates] = useState(!existing);

  const handleTemplateSelect = (template: PolicyTemplate) => {
    const p = template.policy;
    setName(template.name);
    setRuleType(p.ruleType);
    setFloor(p.attendanceFloor);
    setFloorUnit(p.attendanceFloorUnit);
    setUnexcusedGrace(p.grace.unexcusedGrace);
    setExcusedGrace(p.grace.excusedGrace);
    setTardyEnabled(p.tardyConversion.enabled);
    setTardiesPer(p.tardyConversion.tardiesPerAbsence);
    setExcusedLimit(p.excusedAbsenceLimit ?? null);
    setTiers(p.tiers.map((t) => ({ ...t, id: generateId() })));
    setShowTemplates(false);
  };

  const handleSave = () => {
    if (!name.trim()) return;

    const policyData: Omit<AttendancePolicy, 'id'> = {
      name: name.trim(),
      ruleType,
      attendanceFloor: floor,
      attendanceFloorUnit: floorUnit,
      tiers,
      grace: { unexcusedGrace, excusedGrace },
      tardyConversion: { enabled: tardyEnabled, tardiesPerAbsence: tardiesPer },
      excusedAbsenceLimit: excusedLimit,
      partialAbsenceValue: 1.0,
      sessionBasis: 'class_meeting' as any,
    };

    let savedId: string;
    if (existing) {
      updatePolicy(existing.id, policyData);
      savedId = existing.id;
    } else {
      savedId = addPolicy(policyData);
    }
    onSave(savedId);
  };

  return (
    <div className="policy-config-form">
      {showTemplates && !existing && (
        <PolicyTemplateSelector
          onSelect={handleTemplateSelect}
          onScratch={() => setShowTemplates(false)}
        />
      )}

      {!showTemplates && (
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="policy-config-form__fields">
          <Input label="Policy Name" value={name} onChange={(e) => setName(e.target.value)} required />
          <Select label="Rule Type" options={RULE_TYPE_OPTIONS} value={ruleType} onChange={(e) => setRuleType(e.target.value as PolicyRuleType)} hint="How penalties accrue: fixed-count sets a max number of misses, percentage enforces a minimum attendance floor, per-absence deducts points for each miss." />
          <ScrubInput label="Attendance Floor" value={floor} onChange={setFloor} min={0} decimals={0} tooltip="The auto-fail threshold: a count of allowed absences or a minimum attendance percentage (see unit below)." />
          <div className="policy-config-form__inline">
            <label>
              <input type="radio" name="floorUnit" value="count" checked={floorUnit === 'count'} onChange={() => setFloorUnit('count')} />
              Count
            </label>
            <label>
              <input type="radio" name="floorUnit" value="percentage" checked={floorUnit === 'percentage'} onChange={() => setFloorUnit('percentage')} />
              Percentage
            </label>
            <Tooltip content="Whether the floor above is a raw absence count or a minimum attendance percentage.">
              <span className="help-icon" aria-hidden="true">?</span>
            </Tooltip>
          </div>
          <ScrubInput label="Unexcused Grace" value={unexcusedGrace} onChange={setUnexcusedGrace} min={0} decimals={0} tooltip="Free unexcused absences subtracted before any penalty applies." />
          <ScrubInput label="Excused Grace" value={excusedGrace} onChange={setExcusedGrace} min={0} decimals={0} tooltip="Free excused absences subtracted before they count toward penalties." />
          <div className="policy-config-form__toggle">
            <label>
              <input type="checkbox" checked={tardyEnabled} onChange={(e) => setTardyEnabled(e.target.checked)} />
              Tardy Conversion
            </label>
            <Tooltip content="When enabled, late arrivals and early departures combine into full penalty absences.">
              <span className="help-icon" aria-hidden="true">?</span>
            </Tooltip>
            {tardyEnabled && (
              <ScrubInput label="Tardies per Absence" value={tardiesPer} onChange={setTardiesPer} min={1} decimals={0} tooltip="Number of tardies (late + early departure) that convert into one full penalty absence." />
            )}
          </div>
          <div className="policy-config-form__excused-limit">
            <label className="policy-config-form__check">
              <input
                type="checkbox"
                checked={excusedLimit === null}
                onChange={(e) => setExcusedLimit(e.target.checked ? null : 0)}
              />
              Unlimited (no cap)
            </label>
            {excusedLimit !== null && (
              <ScrubInput
                label="Excused Absence Limit"
                value={excusedLimit}
                onChange={setExcusedLimit}
                min={0}
                decimals={0}
                tooltip="Maximum excused absences that count toward penalties; beyond this they are free."
              />
            )}
          </div>

          <PenaltyTierEditor tiers={tiers} onChange={setTiers} />

          <div className="policy-config-form__actions">
            <Button type="submit" variant="primary">Save Policy</Button>
            <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
